import { Injectable } from '@angular/core';
import { Adapter } from '../../../../interfaces/Adapter';
import { RotaRoleAssignment } from '../../../models/rota/RotaRoleAssignment';
import { RotaAvailabilityAssignment, } from '../../../models/rota/RotaAvailabilityAssignment';
import { Rota } from '../../../models/rota/Rota';
import { RotaAssignment } from '../../../models/rota/RotaAssignment';
import {
  RotaAssignmentResponse,
  RotaAvailabilityAssignmentResponse,
  RotaFullDayAbsenceAssignmentResponse,
  RotaHalfDayAbsenceAssignmentResponse,
  RotaResponse,
  RotaRoleAssignmentResponse
} from '../../../../interfaces/response/RotaResponses';
import { EmployeePositionId } from '../../../models/employee/Employee';
import { RotaHalfDayAbsenceAssignment } from '../../../models/rota/RotaHalfDayAbsenceAssignment';
import { RotaFullDayAbsenceAssignment } from '../../../models/rota/RotaFullDayAbsenceAssignment';

/**
 * An adapter converts rota HTTP response to domain object.
 */
@Injectable({
  providedIn: 'root'
})
export class RotaAdapter implements Adapter<Rota> {

  /**
   * Converts rota HTTP response to {@link Rota}.
   * @param item rota HTTP response.
   */
  adapt(item: RotaResponse): Rota {
    const rotaAssignmentAdapter = new RotaAssignmentAdapter();
    const rotaRoleAssignmentAdapter = new RotaRoleAssignmentAdapter();
    const rotaAvailabilityAssignmentAdapter = new RotaAvailabilityAssignmentAdapter();
    const rotaFullDayAbsenceAssignmentAdapter = new RotaFullDayAbsenceAssignmentAdapter();
    const rotaHalfDayAbsenceAssignmentAdapter = new RotaHalfDayAbsenceAssignmentAdapter();
    return new Rota(
      item.rotaAssignments.map(assignment => rotaAssignmentAdapter.adapt(assignment)),
      item.roleAssignments.map(assignment => rotaRoleAssignmentAdapter.adapt(assignment)),
      item.availabilityAssignments.map(assignment => rotaAvailabilityAssignmentAdapter.adapt(assignment)),
      item.fullDayAbsenceAssignments.map(assignment => rotaFullDayAbsenceAssignmentAdapter.adapt(assignment)),
      item.halfDayAbsenceAssignments.map(assignment => rotaHalfDayAbsenceAssignmentAdapter.adapt(assignment))
    );
  }
}

/**
 * An adapter converts rota assignment HTTP response to domain object.
 */
@Injectable({
  providedIn: 'root'
})
export class RotaAssignmentAdapter implements Adapter<RotaAssignment> {

  /**
   * Converts rota assignment HTTP response to {@link RotaAssignment}.
   * @param item rota assignment HTTP response.
   */
  adapt(item: RotaAssignmentResponse): RotaAssignment {
    if (!item) {
      return null;
    }
    return new RotaAssignment(
      new EmployeePositionId(item.employeeId, item.positionId),
      item.dayOfWeek,
      item.startTime,
      item.endTime
    );
  }
}

/**
 * An adapter converts rota role assignment HTTP response to domain object.
 */
@Injectable({
  providedIn: 'root'
})
export class RotaRoleAssignmentAdapter implements Adapter<RotaRoleAssignment> {

  /**
   * Converts rota role assignment HTTP response to {@link RotaRoleAssignment}.
   * @param item rota role assignment HTTP response.
   */
  adapt(item: RotaRoleAssignmentResponse): RotaRoleAssignment {
    if (!item) {
      return null;
    }
    return new RotaRoleAssignment(
      new EmployeePositionId(item.employeeId, item.positionId),
      item.role,
      item.dayOfWeek,
      item.startTime,
      item.endTime
    );
  }
}

/**
 * An adapter converts rota availability assignment HTTP response to domain object.
 */
@Injectable({
  providedIn: 'root'
})
export class RotaAvailabilityAssignmentAdapter implements Adapter<RotaAvailabilityAssignment> {

  /**
   * Converts rota availability assignment HTTP response to {@link RotaAvailabilityAssignment}.
   * @param item rota availability assignment HTTP response.
   */
  adapt(item: RotaAvailabilityAssignmentResponse): RotaAvailabilityAssignment {
    if (!item) {
      return null;
    }
    return new RotaAvailabilityAssignment(
      new EmployeePositionId(item.employeeId, item.positionId),
      item.dayOfWeek,
      item.startTime,
      item.endTime
    );
  }
}

/**
 * An adapter converts rota full day absence HTTP response to domain object.
 */
@Injectable({
  providedIn: 'root'
})
export class RotaFullDayAbsenceAssignmentAdapter implements Adapter<RotaFullDayAbsenceAssignment> {

  /**
   * Converts rota full day absence assignment HTTP response to {@link RotaFullDayAbsenceAssignment}.
   * @param item rota full day absence assignment HTTP response.
   */
  adapt(item: RotaFullDayAbsenceAssignmentResponse): RotaFullDayAbsenceAssignment {
    if (!item) {
      return null;
    }
    return new RotaFullDayAbsenceAssignment(
      new EmployeePositionId(item.employeeId, item.positionId),
      item.dayOfWeek
    );
  }
}

/**
 * An adapter converts rota half day absence HTTP response to domain object.
 */
@Injectable({
  providedIn: 'root'
})
export class RotaHalfDayAbsenceAssignmentAdapter implements Adapter<RotaHalfDayAbsenceAssignment> {

  /**
   * Converts rota half day absence assignment HTTP response to {@link RotaHalfDayAbsenceAssignment}.
   * @param item rota half day absence assignment HTTP response.
   */
  adapt(item: RotaHalfDayAbsenceAssignmentResponse): RotaHalfDayAbsenceAssignment {
    if (!item) {
      return null;
    }
    return new RotaHalfDayAbsenceAssignment(
      new EmployeePositionId(item.employeeId, item.positionId),
      item.dayOfWeek,
      item.startTime,
      item.endTime === '00:00:00' ? '23:59:59' : item.endTime
    );
  }
}
